import { renderToString } from 'react-dom/server';
import { getServerState } from 'react-instantsearch-hooks-server';

import { DynamicComponents, Error, ListOfFlats } from '@/templates';

import { convertToURL } from '@/components/algolia/utils';
import { config } from '@/config';
import { getMinBookingDays, getPage } from '@/graphql/queries';
import { dynamicComponentsTransformer } from '@/graphql/transformers';
import { routes } from '@/lib';
import { generateHierarchical, getPathFromUrl } from '@/utils';

import type { TypeTheme } from '@/types';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type {
  InstantSearchProps,
  InstantSearchServerState,
} from 'react-instantsearch-hooks-web';

type FlatCategoryProps = {
  page: {
    title: string;
    metaDescription: string;
    metaKeywords: null;
    metaRobots: string;
    path: string;
    theme: TypeTheme;
    langcode: {
      id: 'es';
    };
    // TODO: tipar
    attributes: any;
    menu: any[];
    preFooter: any[];
    minBookingDays: number;
  };
  serverState: InstantSearchServerState;
  errorCode?: number;
  defaultArea: string;
  initialUiState: InstantSearchProps['initialUiState'];
  statusCode?: number;
};

const FlatCategory = ({
  serverState,
  defaultArea,
  errorCode,
  initialUiState,
  page: {
    minBookingDays,
    attributes: { Content: dynamicContent },
  },
}: InferGetServerSidePropsType<typeof getServerSideProps>) =>
  errorCode === 404 ? (
    <Error statusCode={errorCode} />
  ) : (
    <>
      <ListOfFlats
        defaultArea={defaultArea}
        serverState={serverState}
        type="MED_EST"
        typeText="de alquiler temporal"
        initialUiState={initialUiState}
        minBookingDays={minBookingDays}
      />

      {dynamicContent?.map(({ __typename, ...props }: any, index: number) => (
        <DynamicComponents
          type={__typename}
          {...props}
          key={`${__typename}-${index}`}
        />
      ))}
    </>
  );

export const getServerSideProps: GetServerSideProps<
  FlatCategoryProps
> = async ({ resolvedUrl, res }) => {
  const minBookingDays = (await getMinBookingDays()) ?? config.minBookingDays;
  const { pages, menusMenus, preFooter } = await getPage(
    routes.flats.half.index
  );
  const attributes = pages.data[0]?.attributes || {};
  attributes.Content = attributes?.Content
    ? dynamicComponentsTransformer(attributes.Content)
    : [];

  const categories = getPathFromUrl(resolvedUrl)
    .split(routes.flats.half.path)
    .join('')
    .split('/')
    .filter(Boolean);
  const {
    hierarchical,
    name: defaultArea,
    path,
  } = generateHierarchical(categories);
  const resultsPath = convertToURL(routes.flats.rent.zone(path));
  const title = `Alquiler pisos media - Pisos de alquiler temporal en ${defaultArea}`;

  const initialUiState = {
    [process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? '']: {
      hierarchicalMenu: {
        'hierarchicalZonas.lvl0': [hierarchical],
      },
    },
  };
  const serverState = await getServerState(
    <ListOfFlats
      initialUiState={initialUiState}
      defaultArea={defaultArea}
      type="MED_EST"
      typeText="de alquiler temporal"
      minBookingDays={minBookingDays}
    />,
    {
      renderToString,
    }
  );

  const results =
    serverState.initialResults[
      process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? ''
    ].results[0];
  const notFound = results.nbHits === 0;

  if (notFound) res.statusCode = 404;

  return {
    props: {
      page: {
        title,
        metaDescription: title,
        metaKeywords: null,
        metaRobots: '',
        path: resultsPath,
        theme: 'light' as TypeTheme,
        langcode: {
          id: 'es',
        },
        attributes,
        minBookingDays,
        menu: menusMenus?.data || [],
        preFooter: preFooter?.data || [],
      },
      serverState,
      defaultArea,
      ...(notFound && { errorCode: 404 }),
      initialUiState,
    },
  };
};

export default FlatCategory;
